import React, { useState, useMemo, useEffect, useRef } from 'react';

const ClassificationSelector = ({ 
  hierarchy = [], 
  selectedFamilyId, 
  onSelect, 
  disabled = false,
  error = null 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  // Aplanar la jerarquía para búsqueda
  const flattenedOptions = useMemo(() => {
    const options = [];
    
    hierarchy.forEach(category => {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach(subcategory => {
          if (subcategory.families && Array.isArray(subcategory.families)) {
            subcategory.families.forEach(family => {
              options.push({
                familyId: family.id,
                familyCode: family.code,
                familyName: family.name,
                subcategoryId: subcategory.id,
                subcategoryCode: subcategory.code,
                subcategoryName: subcategory.name,
                categoryId: category.id,
                categoryCode: category.code,
                categoryName: category.name,
                fullPath: `${category.code} - ${category.name} > ${subcategory.code} - ${subcategory.name} > ${family.code} - ${family.name}`,
                searchText: `${category.code} ${category.name} ${subcategory.code} ${subcategory.name} ${family.code} ${family.name}`.toLowerCase()
              });
            });
          }
        });
      }
    });
    
    return options;
  }, [hierarchy]);

  // Filtrar opciones según búsqueda
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return flattenedOptions;
    }
    
    const search = searchTerm.toLowerCase();
    return flattenedOptions.filter(option => 
      option.searchText.includes(search)
    );
  }, [flattenedOptions, searchTerm]);

  // Obtener la opción seleccionada
  const selectedOption = useMemo(() => {
    if (!selectedFamilyId) return null;
    return flattenedOptions.find(opt => opt.familyId === parseInt(selectedFamilyId));
  }, [flattenedOptions, selectedFamilyId]);

  const handleSelect = (option) => {
    onSelect({
      family_id: option.familyId,
      category_id: option.categoryId,
      subcategory_id: option.subcategoryId
    });
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const clearSelection = () => {
    if (!selectedOption) return;
    onSelect({ family_id: null, category_id: null, subcategory_id: null });
  };

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [isOpen, searchTerm, filteredOptions.length]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const el = optionRefs.current[highlightedIndex];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex, filteredOptions]);

  const handleInputKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;
    const count = filteredOptions.length;

    if (key === 'ArrowDown') {
      event.preventDefault();
      clearSelection();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(count > 0 ? 0 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      clearSelection();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(count > 0 ? count - 1 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
      return;
    }

    if (key === 'Enter') {
      if (!isOpen || highlightedIndex < 0 || !filteredOptions[highlightedIndex]) return;
      event.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
      return;
    }

    if (key === 'Escape' && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Clasificación <span className="text-red-500">*</span>
      </label>
      
      {/* Input de búsqueda */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={selectedOption ? selectedOption.fullPath : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
            if (selectedOption) clearSelection();
          }}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setIsOpen(true)}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          placeholder="Buscar por categoría, subcategoría o familia..."
          disabled={disabled}
          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all pr-10 ${
            error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
              if (!isOpen) setSearchTerm('');
            }
          }}
          disabled={disabled}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown de resultados */}
      {isOpen && !disabled && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => {
              setIsOpen(false);
              setSearchTerm('');
            }}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchTerm ? 'No se encontraron resultados' : 'No hay opciones disponibles'}
              </div>
            ) : (
              <div className="py-2">
                {filteredOptions.map((option, index) => {
                  const isHighlighted = highlightedIndex === index;
                  const isSelected = selectedFamilyId === option.familyId;
                  return (
                  <button
                    key={option.familyId}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted || isSelected}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelect(option)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      isHighlighted
                        ? 'bg-teal-100 ring-1 ring-inset ring-teal-400'
                        : isSelected
                          ? 'bg-teal-50'
                          : 'hover:bg-teal-50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {option.familyCode} - {option.familyName}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">{option.categoryCode} - {option.categoryName}</span>
                          <span className="mx-2">›</span>
                          <span>{option.subcategoryCode} - {option.subcategoryName}</span>
                        </div>
                      </div>
                      {selectedFamilyId === option.familyId && (
                        <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ClassificationSelector;

